﻿import type { ImportGate } from "../types/imports/import-gate.type";
import type {ImportRoute} from "../types/imports/import-routes.types";
import type {ImportMeasurementMoment} from "../types/imports/import-measurement-moment.type";
import type {MeasurementMomentDto} from "../types/dtos/measurement-moment-dto.type";
import type {ImportSectionMeasurements} from "../types/imports/import-section-measurements.type";
import type {SectionMeasurementDto} from "../types/dtos/section-measurement-dto.type";
import type {RouteEditorGate} from "../models/route-editor-gate.model";
import type {RouteEditorRoute} from "../models/route-editor-route.model";
import type {ImportProject} from "../types/imports/import-project.type";
import {postNewProject, putProjectMap} from "../api/projects.api";
import {getOldLisaProjectMeasurements, postImportedMeasurements} from "../api/imports.api";
import type {ProjectMapRequest} from "../models/project-map-request.model";
import type {ImportProjectMap} from "../types/imports/import-project-map.type";
import type { ImportMeasurementsRequestOptions } from "../types/imports/import-measurements-rquest-options.type";
import {getAppConfig} from "./app-config.helper";

// We've agreed with Buko to only import measurements after 2025-07-01
const measurementImportCutoffDate = new Date('2025-07-01T00:00:00.000Z');

export async function importProjectAndMeasurementsFromOldLisa(token: string, importableProject: ImportProject, legacyProjectMap: ImportProjectMap, measurementCount: number)
{
    const largeImportChunkSize = getAppConfig().importChunkSize
    
    // Add project to new Lisa
    const newProjectId = await postNewProject(importableProject.Name);

    const projectMapRequest: ProjectMapRequest = {
        gates: legacyProjectMap.Gates.map(toRouteEditorGate),
        routes: legacyProjectMap.Routes.map(toRouteEditorRoute),
    }

    // Upload the project map to new Lisa!
    try {
        await putProjectMap(newProjectId, projectMapRequest);
    }
    catch(e) {
        console.log(projectMapRequest);
        throw Error(`Error while posting project map request ${importableProject.Name}: ` + e);
    }
    
    if(measurementCount === 0) return;
    
    // Sleep so our the backend has some time to collect Here Routing sections from the API
    await sleep(5000)

    // Fetch sections measurements from old Lisa and map them to new Lisa format
    const importMeasurementsRequestOptions = getImportMeasurementsRequestOptions(importableProject.Id, measurementCount);
    for (const requestOptions of importMeasurementsRequestOptions) {
        const measurements = await getOldLisaProjectMeasurements(token, requestOptions);
        const measurementMoments = measurements.Measurements.map(toMeasurementMomentDto);
        
        try {
            const chunks = chunkArray(measurementMoments, largeImportChunkSize );
            for (const sectionMeasurementMomentChunk of chunks) {
                await postImportedMeasurements(
                    newProjectId, 
                    {sectionMeasurementMoments: sectionMeasurementMomentChunk}
                );
            }
        }
        catch(e) {
            console.log(requestOptions);
            console.log(measurementMoments);
            throw Error(`Error while posting import measurements request ${importableProject.Name}: ` + e);
        }
    }
}

function getImportMeasurementsRequestOptions(projectId: string, measurementCount: number): ImportMeasurementsRequestOptions[] {
    const dateNow = new Date();
    const oneMillion = 1000000;
    if(measurementCount < oneMillion){
        const request = {
            ProjectId: projectId,
            TimestampFrom: measurementImportCutoffDate.toISOString(),
            TimestampTo: dateNow.toISOString()
        }
        return [request]
    }
    
    // If we have more than 1 million measurements, we need to make multiple requests to avoid 413/502/504 errors
    const minTimestamp = measurementImportCutoffDate.toISOString();
    const maxTimestamp = dateNow.toISOString();
    
    const minTimestampAsNumber = new Date(minTimestamp).getTime();
    const maxTimestampAsNumber = new Date(maxTimestamp).getTime();
    const differenceInMilliseconds = maxTimestampAsNumber - minTimestampAsNumber;
    const millisecondsPerRequest = differenceInMilliseconds / 10;
    
    let requests: ImportMeasurementsRequestOptions[] = [];
    
    for (let current = 0; current < differenceInMilliseconds; current += millisecondsPerRequest) {
        const minTimestamp = new Date(minTimestampAsNumber + current).toISOString();
        const maxTimestamp = new Date(minTimestampAsNumber + current + millisecondsPerRequest).toISOString();
        
        requests.push({
            ProjectId: projectId,
            TimestampFrom: minTimestamp,
            TimestampTo: maxTimestamp
        });
    }
    
    return requests;
}

function chunkArray<T>(items: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];

    for (let i = 0; i < items.length; i += chunkSize) {
        chunks.push(items.slice(i, i + chunkSize));
    }

    return chunks;
}

async function sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}

function toMeasurementMomentDto(legacyMoment: ImportMeasurementMoment): MeasurementMomentDto {
    return {
        moment: legacyMoment.Timestamp,
        sectionMeasurements: parseSectionMeasurementDtos(legacyMoment.SectionMeasurements)
    }
}

function parseSectionMeasurementDtos(legacySectionMeasurements: ImportSectionMeasurements): SectionMeasurementDto[] {
    const sectionNames = legacySectionMeasurements.SectionIds.split('\t');
    const durations = legacySectionMeasurements.Durations.split('\t');
    const typicalDurations = legacySectionMeasurements.TypicalDurations.split('\t');

    return sectionNames.flatMap((name, index) => {
        if(legacySectionMeasurements.SectionIds === undefined
            || legacySectionMeasurements.SectionIds.trim() === ''){
            return [];
        }

        const cleanedName = name.replace(':', " - ");

        return [{
            name: name.replace(':', " - "),
            duration: parseRequiredInteger(durations[index], 'duration', cleanedName),
            typicalDuration: parseRequiredInteger(typicalDurations[index], 'typicalDuration', cleanedName)
        }];
    });
}

function parseRequiredInteger(value: string | undefined, fieldName: string, sectionName: string): number {
    if (value === undefined || value.trim() === '') {
        throw new Error(`Missing ${fieldName} for section "${sectionName}"`);
    }

    const parsed = Number.parseInt(value, 10);

    if (!Number.isFinite(parsed)) {
        throw new Error(`Invalid ${fieldName} for section "${sectionName}": "${value}"`);
    }

    return parsed;
}

function toRouteEditorGate(legacyGate: ImportGate): RouteEditorGate {
    return {
        id: null,
        name: legacyGate.Name,
        latitude: legacyGate.Coord.Lat,
        longitude: legacyGate.Coord.Lng
    };
}

function toRouteEditorRoute(legacyRoute: ImportRoute): RouteEditorRoute {
    return {
        id: null,
        name: legacyRoute.Name,
        gateNames: legacyRoute.Gates
    }
}